import cx from 'classnames';
import React, { useEffect } from 'react';
import { NavHubContextProvider } from '/@contexts/nav-hub-context';
import { LeftNavigationHub } from './left-navigation-hub.container';
import styles from './left-navigation-overlay-hub.module.scss';

export interface LeftNavigationOverlayHubInterface {
    hamburgerIconHoverHandler: (
        value: boolean,
        fromHamburgerIcon?: boolean,
        forceToClose?: boolean,
    ) => void;
    showLeftNavigationOverlayHub: boolean;
    themeType: string;
}

export const LeftNavigationOverlayHub: React.FC<LeftNavigationOverlayHubInterface> = ({
    hamburgerIconHoverHandler,
    showLeftNavigationOverlayHub,
    themeType,
}) => {
    const leftNavigationOverlayHubRef = React.useRef(null);

    // When popover is open, the overlay will not be closed on mouse leave.
    // The overlay will only closed on mouse down outside the overlay.
    const outsideMouseDownToHideLeftNavigationOverlayHub = (
        event: MouseEvent,
    ) => {
        let timeout = 50;
        const popoverElement = document.querySelector('.popover-module__in');
        if (!popoverElement) {
            return;
        }

        const mouseDownInsidePanel = leftNavigationOverlayHubRef.current?.contains(
            event.target as Node,
        );
        if (mouseDownInsidePanel) {
            return;
        }

        // When mouse down inside the popover, need to wait to close the popover
        const mouseDownInsidePopoverElement = popoverElement.contains(
            event.target as Node,
        );
        if (mouseDownInsidePopoverElement) {
            timeout = 300;
        }

        // Should wait for the create new options popover to be closed
        setTimeout(() => {
            hamburgerIconHoverHandler(false, false, true);
        }, timeout);
    };

    const showLeftNavigationOverlayHubOnMouseEnter = () => {
        hamburgerIconHoverHandler(true);
    };

    const hideLeftNavigationOverlayHubOnMouseLeave = () => {
        hamburgerIconHoverHandler(false);
    };

    useEffect(() => {
        if (showLeftNavigationOverlayHub) {
            document.addEventListener(
                'mousedown',
                outsideMouseDownToHideLeftNavigationOverlayHub,
            );
        } else {
            document.removeEventListener(
                'mousedown',
                outsideMouseDownToHideLeftNavigationOverlayHub,
            );
        }
        return () => {
            document.removeEventListener(
                'mousedown',
                outsideMouseDownToHideLeftNavigationOverlayHub,
            );
        };
    }, [showLeftNavigationOverlayHub]);

    useEffect(() => {
        const el = leftNavigationOverlayHubRef.current;
        if (el) {
            el.addEventListener(
                'mouseenter',
                showLeftNavigationOverlayHubOnMouseEnter,
            );
            el.addEventListener(
                'mouseleave',
                hideLeftNavigationOverlayHubOnMouseLeave,
            );
        }
        return () => {
            if (el) {
                el.removeEventListener(
                    'mouseenter',
                    showLeftNavigationOverlayHubOnMouseEnter,
                );
                el.removeEventListener(
                    'mouseleave',
                    hideLeftNavigationOverlayHubOnMouseLeave,
                );
            }
        };
    }, []);

    return (
        <div
            className={cx(styles.leftNavigationOverlayHub, {
                [styles.open]: showLeftNavigationOverlayHub,
            })}
            ref={leftNavigationOverlayHubRef}
        >
            <NavHubContextProvider>
                <LeftNavigationHub renderFromOverlay themeType={themeType} />
            </NavHubContextProvider>
        </div>
    );
};
